import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BOAuthProvider, useBOAuth } from './context/BOAuthContext';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import MarketerDashboard from './pages/MarketerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminSettings from './pages/AdminSettings';
import AdminTeamsAndTrainings from './pages/AdminTeamsAndTrainings';
import MarketerDetail from './pages/MarketerDetail';
import JoinLandingPage from './pages/JoinLandingPage';
import BOLogin from './pages/backoffice/BOLogin';
import BODashboard from './pages/backoffice/BODashboard';
import BOUsers from './pages/backoffice/BOUsers';
import BOClients from './pages/backoffice/BOClients';
import BOClientDetail from './pages/backoffice/BOClientDetail';
import BOPlots from './pages/backoffice/BOPlots';
import BOSales from './pages/backoffice/BOSales';
import BOSaleDetail from './pages/backoffice/BOSaleDetail';
import BOPayments from './pages/backoffice/BOPayments';
import BOReceipt from './pages/backoffice/BOReceipt';
import BOExpenses from './pages/backoffice/BOExpenses';
import BOOfficeExpenses from './pages/backoffice/BOOfficeExpenses';
import BOFinancialDashboard from './pages/backoffice/BOFinancialDashboard';
import BOReports from './pages/backoffice/BOReports';
import BORealtors from './pages/backoffice/BORealtors';
import BOAllocationLetter from './pages/backoffice/BOAllocationLetter';
import BOAllocations from './pages/backoffice/BOAllocations';
import BODocuments from './pages/backoffice/BODocuments';
import BORealtorDetail from './pages/backoffice/BORealtorDetail';
import BODevelopmentFees from './pages/backoffice/BODevelopmentFees';
import BODevelopmentFeeReceipt from './pages/backoffice/BODevelopmentFeeReceipt';
import TrackingPixels from './components/TrackingPixels';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, role, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to={role === 'admin' ? '/admin/dashboard' : '/dashboard'} replace />;
  }

  return children;
};

// Public Route - redirects authenticated users
const PublicRoute = ({ children }) => {
  const { isAuthenticated, role, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={role === 'admin' ? '/admin/dashboard' : '/dashboard'} replace />;
  }

  return children;
};

// BO Protected Route
const BOProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, role, loading } = useBOAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/backoffice/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to="/backoffice" replace />;
  }

  return children;
};

// BO Public Route
const BOPublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useBOAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/backoffice" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/join" element={<JoinLandingPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Marketer Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['marketer']}>
            <MarketerDashboard />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/teams-and-training"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminTeamsAndTrainings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/marketers/:id"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <MarketerDetail />
          </ProtectedRoute>
        }
      />

      {/* Back-Office Routes */}
      <Route
        path="/backoffice/login"
        element={
          <BOPublicRoute>
            <BOLogin />
          </BOPublicRoute>
        }
      />
      <Route
        path="/backoffice"
        element={
          <BOProtectedRoute>
            <BODashboard />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/users"
        element={
          <BOProtectedRoute allowedRoles={['owner']}>
            <BOUsers />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/clients"
        element={
          <BOProtectedRoute>
            <BOClients />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/clients/:id"
        element={
          <BOProtectedRoute>
            <BOClientDetail />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/plots"
        element={
          <BOProtectedRoute>
            <BOPlots />
          </BOProtectedRoute>
        }
      />

      <Route
        path="/backoffice/sales"
        element={
          <BOProtectedRoute>
            <BOSales />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/sales/:id"
        element={
          <BOProtectedRoute>
            <BOSaleDetail />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/payments"
        element={
          <BOProtectedRoute>
            <BOPayments />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/receipt/:id"
        element={
          <BOProtectedRoute>
            <BOReceipt />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/expenses"
        element={
          <BOProtectedRoute allowedRoles={['owner']}>
            <BOExpenses />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/office-expenses"
        element={
          <BOProtectedRoute allowedRoles={['owner', 'sales_admin']}>
            <BOOfficeExpenses />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/realtors"
        element={
          <BOProtectedRoute allowedRoles={['owner']}>
            <BORealtors />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/realtors/:id"
        element={
          <BOProtectedRoute allowedRoles={['owner']}>
            <BORealtorDetail />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/allocation/:clientId"
        element={
          <BOProtectedRoute>
            <BOAllocationLetter />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/allocations"
        element={
          <BOProtectedRoute>
            <BOAllocations />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/documents"
        element={
          <BOProtectedRoute>
            <BODocuments />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/reports"
        element={
          <BOProtectedRoute>
            <BOReports />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/financial"
        element={
          <BOProtectedRoute allowedRoles={['owner']}>
            <BOFinancialDashboard />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/development-fees"
        element={
          <BOProtectedRoute>
            <BODevelopmentFees />
          </BOProtectedRoute>
        }
      />
      <Route
        path="/backoffice/development-fees/:id/receipt"
        element={
          <BOProtectedRoute>
            <BODevelopmentFeeReceipt />
          </BOProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BOAuthProvider>
        <BrowserRouter>
          <TrackingPixels />
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
              },
            }}
          />
        </BrowserRouter>
      </BOAuthProvider>
    </AuthProvider>
  );
}

export default App;
